import { useEffect, useReducer, useState } from 'react'

import useAPIRequest from 'src/hooks/useAPIRequest'
import i18n, { getAntdLocale } from 'src/i18n'
import { useUser } from 'src/state/userState'
import { resolveThemeConfig } from 'src/theme-config'

import UIActions from './actions'
import apiClient from './apiClient'
import { UIContext, UIDispatchContext } from './constants'
import initialUIState from './initialState'
import UIReducer from './uiReducer'

import { App, ConfigProvider } from 'antd'

const UIProvider = ({ children }: { children: React.ReactNode }) => {
    const [antdLocale, setAntdLocale] = useState(getAntdLocale())
    const [ui, dispatch] = useReducer(UIReducer, initialUIState)
    const { authenticatedWithFullContext } = useUser()
    const {
        callAPI: getAppSettings,
        loading: loadingAppSettings,
        result: appSettings,
    } = useAPIRequest(apiClient.getAppSettings)

    useEffect(() => {
        if (authenticatedWithFullContext) {
            getAppSettings({})
        }
    }, [])

    useEffect(() => {
        if (appSettings) {
            dispatch({ type: UIActions.STORE_APPLICATION_SETTINGS, payload: appSettings })
        }
    }, [appSettings])

    useEffect(() => {
        const handleLanguageChanged = (lng: string) => {
            setAntdLocale(getAntdLocale(lng))
        }

        i18n.on('languageChanged', handleLanguageChanged)

        return () => {
            i18n.off('languageChanged', handleLanguageChanged)
        }
    }, [])

    return (
        <UIContext.Provider value={{ ...ui, loadingAppSettings: loadingAppSettings }}>
            <UIDispatchContext.Provider value={dispatch}>
                <ConfigProvider
                    theme={resolveThemeConfig(ui)}
                    componentSize="small"
                    space={{ size: 'small' }}
                    locale={antdLocale}
                >
                    <App>{children}</App>
                </ConfigProvider>
            </UIDispatchContext.Provider>
        </UIContext.Provider>
    )
}

export default UIProvider
